/**
 * Days Late Calculator - Turnaround Time Tracker
 * 
 * Calculates the number of days since order creation with status-based freezing logic
 */

/**
 * Calculate Days Late for an order
 * @param {Object} order - Order object with created_at and status
 * @returns {Object} - { daysLate: number, status: string, color: string }
 */
export const calculateDaysLate = (order) => {
  if (!order || !order.created_at) {
    return {
      daysLate: null,
      displayText: 'N/A',
      color: 'gray',
      colorClass: 'text-gray-600 bg-gray-100'
    };
  }

  try {
    // Parse creation date
    const createdDate = new Date(order.created_at);
    const now = new Date();
    
    // Calculate difference in milliseconds and convert to hours
    const diffInMs = now - createdDate;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    // Ensure minimum is 0 (never negative)
    const daysLate = Math.max(0, diffInDays);
    
    // Check if status should freeze the counter
    const frozenStatuses = ['complete', 'completed', 'clarification', 'mail_away', 'mail-away'];
    const isFrozen = frozenStatuses.includes(order.status?.toLowerCase());
    
    // Determine color based on hours/days late
    // 🟢 Green = 0-5 hours (On Track)
    // 🟠 Orange = 5-12 hours (Slight Delay)
    // 🔴 Red = 12+ hours / 1+ days (Delayed)
    // ⚪ Gray = Frozen status
    let color, colorClass;
    if (diffInHours <= 5) {
      color = 'green';
      colorClass = 'text-green-700 bg-green-100 border-green-200';
    } else if (diffInHours <= 12) {
      color = 'orange';
      colorClass = 'text-orange-700 bg-orange-100 border-orange-200';
    } else {
      color = 'red';
      colorClass = 'text-red-700 bg-red-100 border-red-200';
    }
    
    // If frozen, use gray color
    if (isFrozen) {
      colorClass = 'text-gray-700 bg-gray-100 border-gray-300';
    }
    
    return {
      daysLate,
      displayText: daysLate.toString(),
      color,
      colorClass,
      isFrozen,
      status: order.status,
      diffInHours: Math.floor(diffInHours) // For debugging/display
    };
    
  } catch (error) {
    console.error('Error calculating days late:', error);
    return {
      daysLate: null,
      displayText: 'N/A',
      color: 'gray',
      colorClass: 'text-gray-600 bg-gray-100'
    };
  }
};

/**
 * Get descriptive label for days late status based on hours
 * @param {Object} daysLateInfo - Result from calculateDaysLate
 * @returns {string} - Descriptive label
 */
export const getDaysLateLabel = (daysLateInfo) => {
  if (!daysLateInfo || daysLateInfo.daysLate === null) return '';
  
  const hours = daysLateInfo.diffInHours || 0;
  
  if (hours <= 5) return 'On Track';
  if (hours <= 12) return 'Slight Delay';
  return 'Delayed';
};

/**
 * Format days late for display
 * @param {Object} order - Order object
 * @returns {string} - Formatted string like "Days Late: 5"
 */
export const formatDaysLate = (order) => {
  const result = calculateDaysLate(order);
  return `Days Late: ${result.displayText}`;
};
